package com.schoolconnect.app.feature.schools.edit

import com.schoolconnect.app.feature.schools.isValidSchoolPhoneNumber
import com.schoolconnect.app.feature.schools.sanitizeSchoolPhoneInput
import com.schoolconnect.app.datasource.network.response.SchoolResponse
import com.schoolconnect.app.datasource.network.request.SchoolUpdateRequest
import com.schoolconnect.app.repository.school.SchoolRepository
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.repository.user.UserRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class EditSchoolViewModel(
    private val schoolCode: String = "",
    initialValues: EditSchoolFormValues = defaultEditSchoolFormValues(),
    private val loadFromRemote: Boolean = true,
    private val onSaved: (EditSchoolFormValues) -> Unit = {}
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var logoUploadJob: kotlinx.coroutines.Job? = null

    private val _uiState = MutableStateFlow(
            EditSchoolUiState(
                original = initialValues,
                form = initialValues,
                validationErrors = validateEditSchool(initialValues),
                hasSubmitted = true,
                isLoading = loadFromRemote && schoolCode.isNotBlank()
            )
        )

    val uiState: StateFlow<EditSchoolUiState> = _uiState.asStateFlow()

    init {
        if (loadFromRemote && schoolCode.isNotBlank()) {
            loadSchool()
        } else {
            loadLogo(initialValues.schoolLogoUrl)
            loadUserNames(initialValues.createdBy, initialValues.updatedBy)
        }
    }

    fun onEvent(event: EditSchoolUiEvent) {
        when (event) {
            EditSchoolUiEvent.RetryLoad -> loadSchool()
            is EditSchoolUiEvent.FieldChanged -> updateField(event.field, event.value)
            is EditSchoolUiEvent.LogoSelected -> uploadSelectedLogo(event.fileName, event.bytes)
            EditSchoolUiEvent.SaveClicked -> save()
            EditSchoolUiEvent.ResetClicked -> reset()
        }
    }

    private fun uploadSelectedLogo(fileName: String, bytes: ByteArray) {
        logoUploadJob?.cancel()
        _uiState.update {
            it.copy(
                form = it.form.copy(
                    schoolLogoBytes = bytes,
                    schoolLogoFileName = fileName,
                    attachmentFileId = ""
                ),
                isUploadingLogo = true,
                errorMessage = ""
            )
        }
        val schoolId = _uiState.value.form.id.trim().takeIf { it.isNotBlank() }
        logoUploadJob = scope.launch {
            SchoolRepository.uploadSchoolLogoFile(
                schoolId = schoolId,
                fileName = fileName,
                bytes = bytes
            ).fold(
                onSuccess = { upload ->
                    _uiState.update { it.copy(form = it.form.copy(attachmentFileId = upload.fileId), isUploadingLogo = false) }
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isUploadingLogo = false,
                            errorMessage = throwable.message ?: "Failed to upload school logo."
                        )
                    }
                }
            )
        }
    }

    private fun updateField(field: EditSchoolField, value: String) {
        _uiState.update { state ->
            val updated = when (field) {
                EditSchoolField.STATUS -> state.form.copy(status = value)
                EditSchoolField.SCHOOL_NAME -> state.form.copy(schoolName = value)
                EditSchoolField.SCHOOL_CODE -> state.form.copy(schoolCode = value)
                EditSchoolField.BOARD -> state.form.copy(board = value)
                EditSchoolField.TYPE -> state.form.copy(type = value)
                EditSchoolField.MEDIUM -> state.form.copy(medium = value)
                EditSchoolField.CREATED_ON -> state.form.copy(createdOn = value)
                EditSchoolField.CREATED_BY -> state.form.copy(createdBy = value)
                EditSchoolField.UPDATED_ON -> state.form.copy(updatedOn = value)
                EditSchoolField.UPDATED_BY -> state.form.copy(updatedBy = value)
                EditSchoolField.ADDRESS_LINE1 -> state.form.copy(addressLine1 = value)
                EditSchoolField.ADDRESS_LINE2 -> state.form.copy(addressLine2 = value)
                EditSchoolField.CITY -> state.form.copy(city = value)
                EditSchoolField.DISTRICT -> state.form.copy(district = value)
                EditSchoolField.STATE -> state.form.copy(state = value)
                EditSchoolField.COUNTRY -> state.form.copy(country = value)
                EditSchoolField.PIN_CODE -> state.form.copy(pinCode = value)
                EditSchoolField.SCHOOL_PHONE -> state.form.copy(schoolPhone = sanitizeSchoolPhoneInput(value))
                EditSchoolField.SCHOOL_EMAIL -> state.form.copy(schoolEmail = value)
                EditSchoolField.PRIMARY_CONTACT -> state.form.copy(primaryContact = value)
                EditSchoolField.PRIMARY_PHONE -> state.form.copy(primaryPhone = sanitizeSchoolPhoneInput(value))
                EditSchoolField.PRIMARY_EMAIL -> state.form.copy(primaryEmail = value)
                EditSchoolField.SECONDARY_CONTACT -> state.form.copy(secondaryContact = value)
                EditSchoolField.SECONDARY_PHONE -> state.form.copy(secondaryPhone = sanitizeSchoolPhoneInput(value))
                EditSchoolField.SECONDARY_EMAIL -> state.form.copy(secondaryEmail = value)
                EditSchoolField.SCHOOL_DESCRIPTION -> state.form.copy(schoolDescription = value)
            }

            val validation = if (state.hasSubmitted) validateEditSchool(updated) else state.validationErrors
            state.copy(
                form = updated,
                validationErrors = validation
            )
        }
    }

    private fun save() {
        val state = _uiState.value
        if (state.isUploadingLogo) {
            _uiState.update {
                it.copy(
                    hasSubmitted = true,
                    errorMessage = "Please wait for the logo upload to finish."
                )
            }
            return
        }
        val validation = validateEditSchool(state.form)
        if (validation.hasAnyError) {
            _uiState.update {
                it.copy(
                    hasSubmitted = true,
                    validationErrors = validation,
                    isSaving = false,
                    errorMessage = ""
                )
            }
            return
        }

        _uiState.update {
            it.copy(
                hasSubmitted = true,
                validationErrors = validation,
                isSaving = true,
                errorMessage = ""
            )
        }

        scope.launch {
            val saved = _uiState.value.form
            val logoFileId = saved.attachmentFileId
            // Existing logos are downloaded for preview and have no selected file name.
            // Only block save when the user selected a new file that has not completed upload.
            if (saved.schoolLogoFileName.isNotBlank() && logoFileId.isBlank()) {
                _uiState.update { it.copy(isSaving = false, errorMessage = "Please wait for the logo upload to finish.") }
                return@launch
            }
            val persisted = saved.copy(attachmentFileId = logoFileId)
            // The logo endpoint confirms and associates the file with the school.
            // Do not send the logo id again as a school update field.
            val updateRequest = persisted.toSchoolUpdateRequest(_uiState.value.original)
            val statusChanged = persisted.status.trim() != _uiState.value.original.status.trim()
            if (!updateRequest.hasUpdatePayload() && !statusChanged) {
                _uiState.update {
                    it.copy(
                        original = persisted,
                        form = persisted,
                        validationErrors = validateEditSchool(persisted),
                        hasSubmitted = false,
                        isSaving = false,
                        errorMessage = ""
                    )
                }
                onSaved(persisted)
                return@launch
            }
            val result = if (updateRequest.hasUpdatePayload()) {
                SchoolRepository.updateSchool(persisted.schoolCode, updateRequest)
                    .map { Unit }
            } else {
                Result.success(Unit)
            }
            result.fold(
                onSuccess = {
                    scope.launch {
                        val statusResult = if (statusChanged) {
                            SchoolRepository.toggleSchoolStatus(
                                persisted.schoolCode,
                                persisted.status.equals(
                                    com.schoolconnect.app.domain.SchoolKeys.V_STATUS_ACTIVE,
                                    ignoreCase = true
                                )
                            )
                        } else {
                            Result.success(Unit)
                        }
                        statusResult.fold(
                            onSuccess = {
                                _uiState.update {
                                    it.copy(
                                        original = persisted,
                                        form = persisted,
                                        validationErrors = validateEditSchool(persisted),
                                        hasSubmitted = false,
                                        isSaving = false,
                                        errorMessage = ""
                                    )
                                }
                                onSaved(persisted)
                            },
                            onFailure = { throwable ->
                                _uiState.update {
                                    it.copy(
                                        isSaving = false,
                                        errorMessage = throwable.message ?: "Failed to update school status."
                                    )
                                }
                            }
                        )
                    }
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isSaving = false,
                            errorMessage = throwable.message ?: "Failed to update school."
                        )
                    }
                }
            )
        }
    }

    private fun reset() {
        val original = _uiState.value.original
        _uiState.update {
            it.copy(
                form = original,
                validationErrors = validateEditSchool(original),
                hasSubmitted = true,
                isSaving = false,
                errorMessage = ""
            )
        }
    }

    private fun loadSchool() {
        val code = schoolCode.trim()
        if (code.isBlank()) return

        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = ""
            )
        }

        scope.launch {
            val result = SchoolRepository.loadSchoolByCode(code, forceRemote = true)
            result.fold(
                onSuccess = { school ->
                    val loaded = school.toEditSchoolFormValues()
                    _uiState.update {
                        it.copy(
                            original = loaded,
                            form = loaded,
                            validationErrors = validateEditSchool(loaded),
                hasSubmitted = true,
                            isSaving = false,
                            isLoading = false,
                            isCreatedByLoading = false,
                            isUpdatedByLoading = false,
                            errorMessage = ""
                        )
                    }
                    val logoUrl = loaded.schoolLogoUrl.trim()
                    loadLogo(logoUrl)
                    loadUserNames(loaded.createdBy, loaded.updatedBy)
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Failed to load school details."
                        )
                    }
                }
            )
        }
    }

    private fun loadLogo(url: String) {
        val normalizedUrl = url.trim()
        if (normalizedUrl.isBlank()) return
        scope.launch {
            FileUploadRepository.downloadBytes(normalizedUrl).onSuccess { bytes ->
                _uiState.update { state ->
                    if (state.form.schoolLogoBytes != null) {
                        state
                    } else {
                        val withLogo = state.form.copy(schoolLogoBytes = bytes)
                        state.copy(
                            original = state.original.copy(schoolLogoBytes = bytes),
                            form = withLogo
                        )
                    }
                }
            }
        }
    }

    private fun loadUserNames(createdById: String, updatedById: String) {
        loadUserName(createdById, isCreatedBy = true)
        loadUserName(updatedById, isCreatedBy = false)
    }

    private fun loadUserName(userId: String, isCreatedBy: Boolean) {
        val normalizedUserId = userId.trim()
        if (normalizedUserId.isBlank()) {
            _uiState.update {
                if (isCreatedBy) it.copy(form = it.form.copy(createdBy = "-"), original = it.original.copy(createdBy = "-"))
                else it.copy(form = it.form.copy(updatedBy = "-"), original = it.original.copy(updatedBy = "-"))
            }
            return
        }
        _uiState.update { if (isCreatedBy) it.copy(isCreatedByLoading = true) else it.copy(isUpdatedByLoading = true) }
        scope.launch {
            UserRepository.loadUserDisplayName(normalizedUserId).fold(
                onSuccess = { name ->
                    val displayName = name.ifBlank { "-" }
                    _uiState.update {
                        if (isCreatedBy) it.copy(form = it.form.copy(createdBy = displayName), original = it.original.copy(createdBy = displayName), isCreatedByLoading = false)
                        else it.copy(form = it.form.copy(updatedBy = displayName), original = it.original.copy(updatedBy = displayName), isUpdatedByLoading = false)
                    }
                },
                onFailure = {
                    _uiState.update {
                        if (isCreatedBy) it.copy(form = it.form.copy(createdBy = "-"), original = it.original.copy(createdBy = "-"), isCreatedByLoading = false)
                        else it.copy(form = it.form.copy(updatedBy = "-"), original = it.original.copy(updatedBy = "-"), isUpdatedByLoading = false)
                    }
                }
            )
        }
    }

    private fun SchoolResponse.toEditSchoolFormValues(): EditSchoolFormValues {
        return EditSchoolFormValues(
            id = id.orEmpty(),
            status = if (isActive == true) com.schoolconnect.app.domain.SchoolKeys.V_STATUS_ACTIVE else com.schoolconnect.app.domain.SchoolKeys.V_STATUS_INACTIVE,
            schoolName = name.orEmpty(),
            schoolCode = code.orEmpty(),
            board = board.orEmpty(),
            type = schoolType.orEmpty(),
            medium = medium.orEmpty(),
            createdOn = formatEditSchoolDate(createdAt.orEmpty()),
            createdBy = createdById.orEmpty(),
            updatedOn = formatEditSchoolDate(updatedAt.orEmpty()),
            updatedBy = updatedById.orEmpty(),
            addressLine1 = addressLine1.orEmpty(),
            addressLine2 = addressLine2.orEmpty(),
            city = city.orEmpty(),
            district = district.orEmpty(),
            state = state.orEmpty(),
            country = country.orEmpty(),
            pinCode = pinCode.orEmpty(),
            schoolPhone = schoolPhone.orEmpty(),
            schoolEmail = schoolEmail.orEmpty(),
            primaryContact = primaryContactName.orEmpty(),
            primaryPhone = primaryContactPhone.orEmpty(),
            primaryEmail = primaryContactEmail.orEmpty(),
            secondaryContact = secondaryContactName.orEmpty(),
            secondaryPhone = secondaryContactPhone.orEmpty(),
            secondaryEmail = secondaryContactEmail.orEmpty(),
            attachmentFileId = attachmentFileIds?.firstOrNull().orEmpty(),
            schoolDescription = schoolDescription.orEmpty(),
            schoolLogoUrl = schoolLogoUrl.orEmpty()
        )
    }

    fun clear() {
        logoUploadJob?.cancel()
        scope.cancel()
    }
}

    private fun EditSchoolFormValues.toSchoolUpdateRequest(
        original: EditSchoolFormValues
    ): SchoolUpdateRequest {
        val clearFields = linkedSetOf<String>()

        fun changedString(current: String, previous: String): String? {
            val normalizedCurrent = current.trim()
            val normalizedPrevious = previous.trim()
            return normalizedCurrent.takeIf { it != normalizedPrevious }
        }

        fun changedNullableString(key: String, current: String, previous: String): String? {
            val normalizedCurrent = current.trim().ifBlank { null }
            val normalizedPrevious = previous.trim().ifBlank { null }
            if (normalizedCurrent != normalizedPrevious && normalizedCurrent == null) {
                clearFields += key
            }
            return if (normalizedCurrent != normalizedPrevious) normalizedCurrent else null
        }

        return SchoolUpdateRequest(
            name = changedString(schoolName, original.schoolName),
            board = changedString(board, original.board),
            schoolType = changedString(type, original.type),
            medium = changedString(medium, original.medium),
            addressLine1 = changedString(addressLine1, original.addressLine1),
            addressLine2 = changedNullableString("address_line2", addressLine2, original.addressLine2),
            city = changedString(city, original.city),
            district = changedString(district, original.district),
            state = changedString(state, original.state),
            country = changedString(country, original.country),
            pinCode = changedString(pinCode, original.pinCode),
            schoolPhone = changedString(schoolPhone, original.schoolPhone),
            schoolEmail = changedString(schoolEmail, original.schoolEmail),
            primaryContactName = changedString(primaryContact, original.primaryContact),
            primaryContactPhone = changedString(primaryPhone, original.primaryPhone),
            primaryContactEmail = changedString(primaryEmail, original.primaryEmail),
            secondaryContactName = changedNullableString("secondary_contact_name", secondaryContact, original.secondaryContact),
            secondaryContactPhone = changedNullableString("secondary_contact_phone", secondaryPhone, original.secondaryPhone),
            secondaryContactEmail = changedNullableString("secondary_contact_email", secondaryEmail, original.secondaryEmail),
            schoolDescription = changedNullableString("school_description", schoolDescription, original.schoolDescription),
            clearFields = clearFields
        )
    }

    private fun SchoolUpdateRequest.hasUpdatePayload(): Boolean =
        listOf(
            name,
            board,
            schoolType,
            medium,
            addressLine1,
            addressLine2,
            city,
            district,
            state,
            country,
            pinCode,
            schoolPhone,
            schoolEmail,
            primaryContactName,
            primaryContactPhone,
            primaryContactEmail,
            secondaryContactName,
            secondaryContactPhone,
            secondaryContactEmail,
            schoolDescription
        ).any { !it.isNullOrBlank() } || clearFields.isNotEmpty()

private val phoneRegex = Regex("^(\\+91)?[6-9]\\d{9}$")
private val emailRegex = Regex("^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$")

internal fun validateEditSchool(values: EditSchoolFormValues): EditSchoolValidationErrors {
    fun required(value: String, message: String): String =
        if (value.trim().isBlank()) message else ""

    fun optionalEmail(value: String, message: String): String =
        if (value.trim().isBlank()) "" else if (emailRegex.matches(value.trim())) "" else message

    fun requiredEmail(value: String, message: String): String =
        if (value.trim().isBlank()) message else if (emailRegex.matches(value.trim())) "" else message

    fun requiredPhone(value: String, message: String): String =
        if (value.trim().isBlank()) message else if (phoneRegex.matches(value.trim())) "" else message

    fun requiredSchoolPhone(value: String, message: String): String =
        if (value.trim().isBlank()) message else if (isValidSchoolPhoneNumber(value)) "" else message

    fun optionalSchoolPhone(value: String, message: String): String =
        if (value.trim().isBlank()) "" else if (isValidSchoolPhoneNumber(value)) "" else message

    fun optionalPhone(value: String, message: String): String =
        if (value.trim().isBlank()) "" else if (phoneRegex.matches(value.trim())) "" else message

    fun requiredPin(value: String): String =
        if (value.trim().isBlank()) {
            "Pin code is required"
        } else if (value.trim().length < 5 || !value.trim().all(Char::isDigit)) {
            "Enter a valid pin code"
        } else {
            ""
        }

    return EditSchoolValidationErrors(
        status = required(values.status, "Status is required"),
        schoolName = required(values.schoolName, "School name is required"),
        schoolCode = required(values.schoolCode, "School code is required"),
        board = required(values.board, "Board is required"),
        type = required(values.type, "Type is required"),
        medium = required(values.medium, "Medium is required"),
        addressLine1 = required(values.addressLine1, "Address line 1 is required"),
        city = required(values.city, "City is required"),
        district = required(values.district, "District is required"),
        state = required(values.state, "State is required"),
        country = required(values.country, "Country is required"),
        pinCode = requiredPin(values.pinCode),
        schoolPhone = requiredSchoolPhone(values.schoolPhone, "Enter a valid 10-digit phone number"),
        schoolEmail = requiredEmail(values.schoolEmail, "Enter a valid school email"),
        primaryContact = required(values.primaryContact, "Primary contact is required"),
        primaryPhone = requiredSchoolPhone(values.primaryPhone, "Enter a valid 10-digit primary phone"),
        primaryEmail = requiredEmail(values.primaryEmail, "Primary email is required"),
        secondaryPhone = optionalSchoolPhone(values.secondaryPhone, "Enter a valid 10-digit secondary phone"),
        secondaryEmail = optionalEmail(values.secondaryEmail, "Enter a valid secondary email")
    )
}
